/**
 * LifecycleTab
 *
 * Fetches lifecycle data when the tab is rendered, then wires it
 * with useLifecycleAnimation into the map, slider, and controls.
 *
 * States:
 * - No date range: shows informational message
 * - Loading: shows spinner
 * - Error: shows error message
 * - Loaded: shows full lifecycle visualizer with daily polygons
 */
import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCloudSunRain, faDownload, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { WildfireEntity } from '../../../../types/wildfire';
import { useLifecycleData } from '../../../../hooks/useLifecycleData';
import { useLifecycleAnimation } from '../../../../hooks/useLifecycleAnimation';
import { useTools } from '../../../../context/ToolContext';
import { getBackendUrl } from '../../../../services/configService';
import { LifecycleMap } from '../lifecycle/LifecycleMap';
import { LifecycleSlider } from '../lifecycle/LifecycleSlider';
import { LifecycleControls } from '../lifecycle/LifecycleControls';
import { todayInNwt } from '../../../../utils/nwtDate';
import './TabCommon.scss';

interface LifecycleTabProps {
  wildfire: WildfireEntity;
}

export const LifecycleTab: React.FC<LifecycleTabProps> = ({ wildfire }) => {
  const {
    startDate,
    endDate,
    origin,
    frames,
    loading,
    error,
  } = useLifecycleData(wildfire);

  const {
    selectedDate,
    playing,
    speed,
    play,
    pause,
    stepForward,
    stepBack,
    setSpeed,
    setDate,
  } = useLifecycleAnimation(startDate, endDate);

  const { openTool } = useTools();
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [nearestStation, setNearestStation] = useState<{ nesdis: string; name: string; distance_m: number; dataSource: string } | null>(null);
  const [weatherChecked, setWeatherChecked] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Eagerly check for nearest station with data when lifecycle loads
  useEffect(() => {
    if (!origin || !startDate || weatherChecked) return;

    const [lon, lat] = origin;
    const params = new URLSearchParams({ lat: String(lat), lon: String(lon) });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    setWeatherLoading(true);
    fetch(`${getBackendUrl()}/api/weather-stations/nearest?${params.toString()}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(station => {
        if (station) {
          setNearestStation({
            nesdis: station.nesdis,
            name: station.name || station.nesdis,
            distance_m: station.distance_m,
            dataSource: station.dataSource || 'none',
          });
        }
        setWeatherChecked(true);
      })
      .catch(() => setWeatherChecked(true))
      .finally(() => setWeatherLoading(false));
  }, [origin, startDate, endDate, weatherChecked]);

  const weatherAvailable = nearestStation != null && nearestStation.dataSource !== 'none';

  const handleWeatherClick = useCallback(() => {
    if (!nearestStation || !weatherAvailable) return;
    openTool('weatherData', { stationId: nearestStation.nesdis, startDate: startDate ?? undefined, endDate: endDate ?? undefined });
  }, [nearestStation, weatherAvailable, startDate, endDate, openTool]);

  const handleDownload = useCallback((format: string, crs: string) => {
    setShowDownloadMenu(false);
    const params = new URLSearchParams({ startDate: startDate! });
    if (endDate) params.set('outDate', endDate);
    if (origin) {
      params.set('lat', String(origin[1]));
      params.set('lon', String(origin[0]));
    }
    params.set('format', format);
    params.set('crs', crs);

    const url = `${getBackendUrl()}/api/lifecycle/${encodeURIComponent(wildfire.fire_number)}/download?${params.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [startDate, endDate, origin, wildfire.fire_number]);

  // No date range available
  if (!loading && !error && !startDate && !endDate) {
    return (
      <div className="wildfire-detail-content lifecycle-tab">
        <div className="lifecycle-tab__empty">
          <p>No date range available for this fire. Lifecycle visualization requires a start date.</p>
        </div>
      </div>
    );
  }

  // Data loaded but no usable polygons (no perimeters, no hotspots in area)
  if (!loading && !error && frames.length > 0 && frames.every(f => !f.polygon)) {
    return (
      <div className="wildfire-detail-content lifecycle-tab">
        <div className="lifecycle-tab__empty">
          <p>No perimeter or hotspot data available for this fire's lifecycle.</p>
        </div>
      </div>
    );
  }

  // No frames at all
  if (!loading && !error && frames.length === 0 && startDate) {
    return (
      <div className="wildfire-detail-content lifecycle-tab">
        <div className="lifecycle-tab__empty">
          <p>No lifecycle data found for this fire.</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="wildfire-detail-content lifecycle-tab">
        <div className="lifecycle-tab__loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '40px 0', color: '#999' }}>
          <FontAwesomeIcon icon={faSpinner} spin size="lg" />
          <span>Loading lifecycle data&hellip;</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="wildfire-detail-content lifecycle-tab">
        <div className="lifecycle-tab__error">
          <p>Error loading lifecycle data: {error}</p>
        </div>
      </div>
    );
  }

  const effectiveStart = startDate!;
  const effectiveEnd = endDate ?? todayInNwt();

  // Find the current frame for status display
  const currentFrame = frames.find(f => f.date === (selectedDate || effectiveStart));
  const gpsCount = frames.filter(f => f.source === 'gps' && f.polygon).length;
  const hotspotCount = frames.filter(f => f.source === 'hotspot' && f.polygon).length;
  const emptyCount = frames.filter(f => !f.polygon).length;

  return (
    <div className="wildfire-detail-content lifecycle-tab">
      <div className="lifecycle-tab__status" style={{ padding: '4px 8px', fontSize: '12px', color: '#999', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <span>
          <span style={{ color: '#ff6600', fontWeight: 700 }}>{gpsCount}</span> GPS days
        </span>
        <span>
          <span style={{ color: '#cc0000', fontWeight: 700 }}>{hotspotCount}</span> hotspot days
        </span>
        <span>
          <span style={{ fontWeight: 700 }}>{emptyCount}</span> no data
        </span>

        <button
          className="btn btn-sm btn-outline"
          style={{ marginLeft: '8px', fontSize: '11px', padding: '2px 8px', opacity: weatherChecked && !weatherAvailable ? 0.5 : 1 }}
          onClick={handleWeatherClick}
          disabled={weatherLoading || !weatherAvailable}
          title={
            weatherLoading ? 'Checking for weather data...'
            : weatherChecked && !weatherAvailable ? 'No weather data available for this period'
            : nearestStation ? `${nearestStation.name} (${(nearestStation.distance_m / 1000).toFixed(1)} km)`
            : 'Weather'
          }
        >
          <FontAwesomeIcon icon={weatherLoading ? faSpinner : faCloudSunRain} spin={weatherLoading} />
          {' '}Weather
        </button>

        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            className="btn btn-sm btn-outline"
            style={{ fontSize: '11px', padding: '2px 8px' }}
            onClick={() => setShowDownloadMenu(prev => !prev)}
            title="Download lifecycle polygons"
          >
            <FontAwesomeIcon icon={faDownload} />
            {' '}Download
            {' '}<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px' }} />
          </button>
          {showDownloadMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 1000,
              background: 'var(--em3-color-background-main, #fff)',
              border: '1px solid var(--em3-color-border, #ddd)',
              borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              minWidth: '200px', marginTop: '2px',
            }}>
              {[
                { label: 'Shapefile (NWT Lambert)', format: 'shapefile', crs: '3580' },
                { label: 'Shapefile (WGS 84)', format: 'shapefile', crs: '4326' },
                { label: 'GeoJSON (WGS 84)', format: 'geojson', crs: '4326' },
              ].map(opt => (
                <button
                  key={opt.format + opt.crs}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 12px', border: 'none', background: 'none',
                    fontSize: '12px', cursor: 'pointer',
                    color: 'var(--em3-color-text-primary, #333)',
                  }}
                  onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--em3-color-background-panel, #f5f5f5)'; }}
                  onMouseLeave={e => { (e.target as HTMLElement).style.background = 'none'; }}
                  onClick={() => handleDownload(opt.format, opt.crs)}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ marginRight: '6px', fontSize: '10px' }} />
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {currentFrame && (
          <span style={{ marginLeft: 'auto', fontWeight: 600, color: currentFrame.source === 'gps' ? '#ff6600' : '#cc0000' }}>
            {selectedDate || effectiveStart} — {currentFrame.source === 'gps' ? 'GPS Perimeter' : 'Hotspot Estimated Perimeter'}
            {currentFrame.areaHa != null && ` (${currentFrame.areaHa.toFixed(1)} ha)`}
          </span>
        )}
      </div>

      <LifecycleMap
        frames={frames}
        selectedDate={selectedDate || effectiveStart}
        origin={origin}
      />

      <div className="lifecycle-tab__controls">
        <LifecycleSlider
          startDate={effectiveStart}
          endDate={effectiveEnd}
          selectedDate={selectedDate || effectiveStart}
          onChange={setDate}
        />

        <LifecycleControls
          playing={playing}
          speed={speed}
          onPlay={play}
          onPause={pause}
          onStepForward={stepForward}
          onStepBack={stepBack}
          onSetSpeed={setSpeed}
        />
      </div>
    </div>
  );
};
